import { CONFIG } from "../config.ts";
import { clamp, TAU } from "../math.ts";
import type { Tilt } from "../systems/tilt.ts";

// The cleansing meditation. You control ONE dot. First bring it to the centre to
// calibrate (this sets your tilt "neutral"), then trace the whole mandala with
// it — the path lights up behind you as you go. Stay on the line; cutting
// corners earns nothing. Complete the full loop before the incense burns out.
// ONLY a full trace cleanses; running out fails and you try again. Each cleanse
// is a level harder: more petals, tighter line, less incense.
//
// Phone: steer by TILTING. Desktop: move the pointer (fallback).

export type CleanseResult = "full" | "fail";
type Phase = "calibrate" | "trace";

const CENTER_R = 42; // px radius of the centering target
const HOLD_NEEDED = 0.6; // s to hold in the centre to begin
const FORWARD_WINDOW = 0.06; // how far ahead on the path the dot may jump

export class Mandala {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private tilt: Tilt;
  private onDone: (r: CleanseResult) => void = () => {};

  private active = false;
  private raf = 0;
  private last = 0;

  private cx = 0;
  private cy = 0;
  private R = 0;
  private petals = 5;
  private tolerance = 30;
  private timeLimit = 11;
  private level = 0;

  private phase: Phase = "calibrate";
  private progress = 0; // furthest path parameter reached, 0..1
  private elapsed = 0; // trace time (incense)
  private centerHold = 0;

  private cur = { x: 0, y: 0 };
  private usingTilt = false;
  private base: { beta: number; gamma: number } | null = null;
  private dpr = 1;

  constructor(parent: HTMLElement, tilt: Tilt) {
    this.tilt = tilt;
    this.canvas = document.createElement("canvas");
    this.canvas.className = "mandala";
    Object.assign(this.canvas.style, {
      position: "absolute",
      inset: "0",
      width: "100%",
      height: "100%",
      zIndex: "50",
      touchAction: "none",
      display: "none",
    } as CSSStyleDeclaration);
    parent.appendChild(this.canvas);
    const ctx = this.canvas.getContext("2d");
    if (!ctx) throw new Error("2D canvas unsupported");
    this.ctx = ctx;

    this.canvas.addEventListener("pointerdown", this.onDown, { passive: false });
    this.canvas.addEventListener("pointermove", this.onMove, { passive: false });
  }

  get isActive(): boolean {
    return this.active;
  }

  start(level: number, onDone: (r: CleanseResult) => void) {
    this.onDone = onDone;
    this.level = level;
    this.resize();

    const m = CONFIG.mandala;
    this.petals = Math.min(m.maxPetals, m.basePetals + m.petalsPerLevel * level);
    this.tolerance = Math.max(m.minTolerance, m.baseTolerance + m.tolerancePerLevel * level);
    this.timeLimit = Math.max(m.minTimeLimit, m.baseTimeLimit + m.timePerLevel * level);
    this.R = Math.min(this.cx, this.cy) * 0.62;

    this.phase = "calibrate";
    this.progress = 0;
    this.elapsed = 0;
    this.centerHold = 0;
    this.base = null;
    this.cur = { x: this.cx, y: this.cy };

    this.active = true;
    this.canvas.style.display = "block";
    this.last = performance.now();
    this.raf = requestAnimationFrame(this.frame);
  }

  private stop(result: CleanseResult) {
    this.active = false;
    cancelAnimationFrame(this.raf);
    this.canvas.style.display = "none";
    this.onDone(result);
  }

  private resize() {
    this.dpr = Math.min(window.devicePixelRatio || 1, 2.5);
    const w = window.innerWidth;
    const h = window.innerHeight;
    this.canvas.width = Math.floor(w * this.dpr);
    this.canvas.height = Math.floor(h * this.dpr);
    this.cx = w / 2;
    this.cy = h / 2;
  }

  private pathPoint(t: number): { x: number; y: number } {
    const a = t * TAU;
    const r = this.R * (0.72 + 0.28 * Math.cos(this.petals * a));
    return { x: this.cx + Math.cos(a) * r, y: this.cy + Math.sin(a) * r };
  }

  /** Nearest path parameter to a screen point, plus that distance. */
  private nearestOnPath(x: number, y: number): { t: number; dist: number } {
    const steps = 260;
    let bestT = 0;
    let bestD = Infinity;
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      const p = this.pathPoint(t);
      const d = (p.x - x) * (p.x - x) + (p.y - y) * (p.y - y);
      if (d < bestD) {
        bestD = d;
        bestT = t;
      }
    }
    return { t: bestT, dist: Math.sqrt(bestD) };
  }

  private setPointer(e: PointerEvent) {
    if (this.tilt.available) return;
    const rect = this.canvas.getBoundingClientRect();
    this.cur.x = e.clientX - rect.left;
    this.cur.y = e.clientY - rect.top;
  }
  private onDown = (e: PointerEvent) => {
    e.preventDefault();
    if (this.tilt.available) this.base = null; // tap to re-centre the tilt neutral
    else this.setPointer(e);
  };
  private onMove = (e: PointerEvent) => {
    if (!this.active) return;
    e.preventDefault();
    this.setPointer(e);
  };

  private frame = (now: number) => {
    if (!this.active) return;
    let dt = (now - this.last) / 1000;
    this.last = now;
    if (dt > 0.05) dt = 0.05;
    this.update(dt);
    this.draw();
    if (this.active) this.raf = requestAnimationFrame(this.frame);
  };

  /** Update the single control dot from tilt (delta from a neutral) or pointer. */
  private updateCursor() {
    if (!this.tilt.available) {
      this.usingTilt = false;
      return; // cur is set by pointer events
    }
    this.usingTilt = true;
    if (!this.base) this.base = { beta: this.tilt.beta, gamma: this.tilt.gamma };
    const lim = CONFIG.mandala.tiltSensitivityDeg;
    const s = (Math.min(this.cx, this.cy) * 0.95) / lim;
    const dg = clamp(this.tilt.gamma - this.base.gamma, -lim, lim);
    const db = clamp(this.tilt.beta - this.base.beta, -lim, lim);
    this.cur.x = clamp(this.cx + dg * s, 0, this.cx * 2);
    this.cur.y = clamp(this.cy + db * s, 0, this.cy * 2);
  }

  private update(dt: number) {
    this.updateCursor();

    if (this.phase === "calibrate") {
      const d = Math.hypot(this.cur.x - this.cx, this.cur.y - this.cy);
      if (d <= CENTER_R) this.centerHold += dt;
      else this.centerHold = Math.max(0, this.centerHold - dt * 2);
      if (this.centerHold >= HOLD_NEEDED) {
        // Lock this pose as neutral so the dot rests centred, then begin.
        if (this.tilt.available) this.base = { beta: this.tilt.beta, gamma: this.tilt.gamma };
        this.phase = "trace";
        this.progress = 0;
        this.elapsed = 0;
      }
      return;
    }

    // trace
    this.elapsed += dt;
    const near = this.nearestOnPath(this.cur.x, this.cur.y);
    const onPath = near.dist <= this.tolerance;
    // Advance only forward, and only a small step at a time (no skipping petals).
    if (onPath && near.t >= this.progress && near.t <= this.progress + FORWARD_WINDOW) {
      this.progress = near.t;
    }

    if (this.progress >= 0.992) this.stop("full");
    else if (this.elapsed >= this.timeLimit) this.stop("fail");
  }

  private draw() {
    const ctx = this.ctx;
    ctx.save();
    ctx.scale(this.dpr, this.dpr);
    const w = window.innerWidth;
    const h = window.innerHeight;

    const g = ctx.createRadialGradient(this.cx, this.cy, 40, this.cx, this.cy, Math.max(w, h) * 0.7);
    g.addColorStop(0, "rgba(20,34,78,0.92)");
    g.addColorStop(1, "rgba(6,10,26,0.97)");
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, w, h);

    ctx.lineCap = "round";
    ctx.lineJoin = "round";

    if (this.phase === "calibrate") {
      // dim path preview
      this.tracePath(ctx, 1);
      ctx.strokeStyle = "rgba(201,154,52,0.18)";
      ctx.lineWidth = 2;
      ctx.stroke();

      // centre target with hold-progress ring
      ctx.strokeStyle = "rgba(233,205,114,0.5)";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(this.cx, this.cy, CENTER_R, 0, TAU);
      ctx.stroke();
      const frac = Math.min(1, this.centerHold / HOLD_NEEDED);
      ctx.strokeStyle = "#f4e2a1";
      ctx.lineWidth = 4;
      ctx.beginPath();
      ctx.arc(this.cx, this.cy, CENTER_R, -Math.PI / 2, -Math.PI / 2 + frac * TAU);
      ctx.stroke();

      this.drawDot(ctx, Math.hypot(this.cur.x - this.cx, this.cur.y - this.cy) <= CENTER_R);

      ctx.textAlign = "center";
      ctx.fillStyle = "#f2e7c8";
      ctx.font = "600 22px Georgia, serif";
      ctx.fillText("Centre yourself", this.cx, 74);
      ctx.font = "400 15px system-ui, sans-serif";
      ctx.fillStyle = "#cdb87e";
      const hint = this.usingTilt ? "tilt to bring the dot to the centre" : "move the dot to the centre";
      ctx.fillText(hint, this.cx, 102);
      ctx.restore();
      return;
    }

    // trace: tolerance track + full path
    this.tracePath(ctx, 1);
    ctx.strokeStyle = "rgba(201,154,52,0.14)";
    ctx.lineWidth = this.tolerance * 2;
    ctx.stroke();
    this.tracePath(ctx, 1);
    ctx.strokeStyle = "rgba(233,205,114,0.35)";
    ctx.lineWidth = 3;
    ctx.stroke();
    // lit trail behind you
    if (this.progress > 0.001) {
      this.tracePath(ctx, this.progress);
      ctx.strokeStyle = "#f4e2a1";
      ctx.lineWidth = 6;
      ctx.stroke();
    }
    // start marker (where to begin) until you're underway
    if (this.progress < 0.06) {
      const s0 = this.pathPoint(0);
      const pulse = 8 + Math.sin(this.elapsed * 6) * 2;
      ctx.strokeStyle = "rgba(255,244,196,0.8)";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(s0.x, s0.y, pulse, 0, TAU);
      ctx.stroke();
    }

    const near = this.nearestOnPath(this.cur.x, this.cur.y);
    this.drawDot(ctx, near.dist <= this.tolerance);

    ctx.textAlign = "center";
    ctx.fillStyle = "#f2e7c8";
    ctx.font = "600 22px Georgia, serif";
    ctx.fillText("Purifying…", this.cx, 74);
    ctx.font = "400 14px system-ui, sans-serif";
    ctx.fillStyle = "#cdb87e";
    const timeLeft = Math.max(0, this.timeLimit - this.elapsed);
    ctx.fillText(
      `level ${this.level + 1}  ·  ${Math.round(this.progress * 100)}%  ·  incense ${timeLeft.toFixed(1)}s`,
      this.cx,
      100,
    );
    ctx.restore();
  }

  /** The single dot the player controls. */
  private drawDot(ctx: CanvasRenderingContext2D, good: boolean) {
    const col = good ? "#8fe3c2" : "#e0663f";
    const glow = ctx.createRadialGradient(this.cur.x, this.cur.y, 2, this.cur.x, this.cur.y, 26);
    glow.addColorStop(0, good ? "rgba(143,227,194,0.7)" : "rgba(224,102,63,0.6)");
    glow.addColorStop(1, "rgba(0,0,0,0)");
    ctx.fillStyle = glow;
    ctx.beginPath();
    ctx.arc(this.cur.x, this.cur.y, 26, 0, TAU);
    ctx.fill();
    ctx.fillStyle = col;
    ctx.beginPath();
    ctx.arc(this.cur.x, this.cur.y, 9, 0, TAU);
    ctx.fill();
    ctx.strokeStyle = "rgba(255,255,255,0.85)";
    ctx.lineWidth = 2;
    ctx.stroke();
  }

  private tracePath(ctx: CanvasRenderingContext2D, upTo: number) {
    ctx.beginPath();
    const steps = 240;
    for (let i = 0; i <= steps; i++) {
      const t = (i / steps) * upTo;
      const pt = this.pathPoint(t);
      if (i === 0) ctx.moveTo(pt.x, pt.y);
      else ctx.lineTo(pt.x, pt.y);
    }
  }
}
